import React from 'react';
import './LegalPages.css';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last updated: November 5, 2025</p>
        
        <div className="legal-content">
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By using Timepass Game, you agree to these terms. If you don't agree, please don't use our service.
            </p>
          </section>

          <section>
            <h2>2. Our Games and Services</h2>
            <p>
              We provide free online games and tools including:
            </p>
            <h4>🎮 Games:</h4>
            <ul>
              <li><strong>Country Knowledge Quiz</strong> - Test your geography knowledge with questions about countries, capitals, and currencies</li>
              <li><strong>Gem Quest</strong> - Match-3 puzzle game with colorful gems and power-ups</li>
              <li><strong>Secret Number Game</strong> - Mathematical puzzle challenges and logical deduction</li>
              <li><strong>Snake Game</strong> - Classic arcade entertainment with modern features</li>
              <li><strong>Image Puzzle Games</strong> - Tiger and Lions sliding puzzles for brain training</li>
            </ul>
            <h4>🛠️ Tools:</h4>
            <ul>
              <li><strong>Character Counter</strong> - Text analysis tool for counting characters, words, and lines</li>
              <li><strong>Greeting Cards Generator</strong> - Create personalized digital greeting cards</li>
              <li><strong>Spin Wheels</strong> - Decision-making tools including Number Spinner, Month Spinner, and Truth or Dare</li>
            </ul>
            <p>
              All services are provided free of charge and are supported by advertising.
            </p>
          </section>

          <section>
            <h2>3. User Conduct and Acceptable Use</h2>
            <p>You agree to use our services responsibly and not to:</p>
            <ul>
              <li>Use our games or tools for any unlawful purposes or illegal activities</li>
              <li>Attempt to hack, cheat, or manipulate games or their scoring systems</li>
              <li>Submit inappropriate usernames, content, or messages</li>
              <li>Interfere with our website's operation or other users' enjoyment</li>
              <li>Use automated systems or bots to play games or access our services</li>
              <li>Attempt to access restricted areas of our website or systems</li>
              <li>Share false, misleading, or harmful information</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon intellectual property rights of others</li>
              <li>Engage in any activity that could damage our reputation or services</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate access for users who violate these terms.
            </p>
          </section>

          <section>
            <h2>4. Game Rules and Fair Play</h2>
            <p>
              Our games are designed for entertainment and educational purposes. We promote fair play and maintain the integrity of our gaming environment by:
            </p>
            <ul>
              <li>Monitoring for suspicious activity and score manipulation</li>
              <li>Removing scores that appear to be artificially inflated or manipulated</li>
              <li>Implementing security measures to prevent cheating</li>
              <li>Reserving the right to reset leaderboards if necessary</li>
            </ul>
            <p>
              Players found violating game rules may have their scores removed and access restricted.
            </p>
          </section>

          <section>
            <h2>5. Advertising and Third-Party Content</h2>
            <p>
              Our free services are supported by advertising. By using our website, you agree that:
            </p>
            <ul>
              <li>We may display advertisements alongside our games and tools</li>
              <li>Third-party advertisers may use cookies and tracking technologies</li>
              <li>We are not responsible for the content of third-party advertisements</li>
              <li>Clicking on ads may redirect you to external websites</li>
              <li>You interact with third-party ads at your own risk</li>
            </ul>
          </section>

          <section>
            <h2>6. Content and Intellectual Property</h2>
            <p>
              <strong>Our Content:</strong> All game content, designs, graphics, code, and text on this website are owned by us or our licensors and are protected by copyright and other intellectual property laws.
            </p>
            <p>
              <strong>User-Generated Content:</strong> When you submit scores, usernames, or other content, you grant us a non-exclusive, royalty-free license to display and use such content for our services, including leaderboards and promotional materials.
            </p>
            <p>
              <strong>Third-Party Content:</strong> Some games may include third-party images or content used under appropriate licenses. We respect intellectual property rights and will respond to valid takedown requests.
            </p>
          </section>

          <section>
            <h2>7. Service Availability and Technical Issues</h2>
            <p>
              While we strive to provide reliable service, we cannot guarantee that our games and tools will be available 24/7 without interruption. Service may be temporarily unavailable due to:
            </p>
            <ul>
              <li>Scheduled maintenance and updates</li>
              <li>Technical difficulties or server issues</li>
              <li>Force majeure events beyond our control</li>
              <li>Security-related suspensions</li>
            </ul>
            <p>
              We will make reasonable efforts to minimize service disruptions and provide notice when possible.
            </p>
          </section>

          <section>
            <h2>8. Disclaimers and Warranties</h2>
            <p>
              Our games and tools are provided "as is" and "as available" without warranties of any kind, either express or implied. We disclaim all warranties, including but not limited to:
            </p>
            <ul>
              <li>Merchantability and fitness for a particular purpose</li>
              <li>Non-infringement of third-party rights</li>
              <li>Uninterrupted or error-free operation</li>
              <li>Accuracy or completeness of content</li>
              <li>Security of data transmission</li>
            </ul>
          </section>

          <section>
            <h2>9. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or goodwill, arising from:
            </p>
            <ul>
              <li>Your use or inability to use our services</li>
              <li>Any technical issues or service interruptions</li>
              <li>Loss of game progress, scores, or data</li>
              <li>Third-party content or advertisements</li>
              <li>Unauthorized access to your information</li>
            </ul>
            <p>
              Our total liability shall not exceed $100 or the amount you paid us in the last 12 months (which is $0 for free services).
            </p>
          </section>

          <section>
            <h2>10. Indemnification</h2>
            <p>
              You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from your use of our services, violation of these terms, or infringement of any third-party rights.
            </p>
          </section>

          <section>
            <h2>11. Privacy and Data Protection</h2>
            <p>
              Your privacy is important to us. Please review our <a href="/privacy-policy">Privacy Policy</a> to understand how we collect, use, and protect your information. By using our services, you consent to our privacy practices.
            </p>
          </section>

          <section>
            <h2>12. Changes to Terms and Services</h2>
            <p>
              We may update these terms from time to time to reflect changes in our services, laws, or business practices. We will post updated terms on this page with a new "Last updated" date. Material changes will be highlighted or communicated through our website.
            </p>
            <p>
              Your continued use of our services after such changes constitutes acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2>13. Termination</h2>
            <p>
              We may suspend or terminate your access to our services at any time, with or without notice, for violations of these terms or other legitimate reasons. Upon termination, your right to use our services will cease immediately.
            </p>
          </section>

          <section>
            <h2>14. Governing Law and Jurisdiction</h2>
            <p>
              These terms shall be governed by and construed in accordance with applicable laws. Any disputes arising from these terms or your use of our services shall be resolved through binding arbitration or in the appropriate courts.
            </p>
          </section>

          <section>
            <h2>15. Severability and Entire Agreement</h2>
            <p>
              If any provision of these terms is found to be unenforceable, the remaining provisions will remain in full force and effect. These terms, together with our Privacy Policy, constitute the entire agreement between you and us regarding our services.
            </p>
          </section>

          <section>
            <h2>16. Contact Information</h2>
            <p>
              If you have questions about these Terms of Service, please contact us:
            </p>
            <ul>
              <li>Through our <a href="/contact">Contact page</a></li>
              <li>Email: legal@timepassgame.com</li>
              <li>We will respond to inquiries within 5-7 business days</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
